import { useContext, useEffect, useRef } from 'react';
import { AffContext } from '../context/AffiliateContext';
import trackingLink from '../services/trackingLink';

// Keep one tracking operation per event + affiliate for the life of this page.
// This protects against StrictMode and accidental route remounts creating duplicate views.
const trackingCache = new Map();

function getTrackingRecord(eventId, aff) {
  const key = `${eventId}:${aff}`;
  const existing = trackingCache.get(key);
  if (existing) return existing;

  const record = {
    id: null,
    promise: null,
  };

  record.promise = Promise.resolve(trackingLink(eventId, aff))
    .then((response) => {
      record.id = response?._id || null;
      return record.id;
    })
    .catch((error) => {
      trackingCache.delete(key);
      throw error;
    });

  trackingCache.set(key, record);
  return record;
}

export default function useAffiliateTracking(eventId) {
  const { aff, setAffId } = useContext(AffContext);
  const trackedKeyRef = useRef(null);

  useEffect(() => {
    if (!aff || !eventId) {
      setAffId(null);
      trackedKeyRef.current = null;
      return undefined;
    }

    const trackingKey = `${eventId}:${aff}`;
    if (trackedKeyRef.current === trackingKey) return undefined;

    let cancelled = false;
    const record = getTrackingRecord(eventId, aff);

    record.promise
      .then((trackingId) => {
        if (cancelled) return;
        setAffId(trackingId);
        trackedKeyRef.current = trackingKey;
      })
      .catch((error) => {
        console.error('affiliate tracking error', error);
      });

    return () => {
      cancelled = true;
    };
  }, [aff, eventId, setAffId]);
}
